import URI from '@dw/urijs-esm';
import * as router from '../redux/router';

// Opened dialogs, oldest first, so a back gesture can close the one on top.
const openedDialogs = [];

/**
 * @returns {Element} The dialog opened last that is still open, or `undefined`.
 */
export const topOpenedDialog = () => openedDialogs[openedDialogs.length - 1];

/**
 * @returns {Boolean} `true` when another dialog is open under the top one.
 */
export const hasDialogUnderTop = () => openedDialogs.length > 1;

const removeOpenedDialog = (dialog) => {
  const index = openedDialogs.indexOf(dialog);
  index !== -1 && openedDialogs.splice(index, 1);
};

// A browser back takes a dialog's `?action` away; one opened by a tap (a toolbar, a select) closes here.
// Dialogs sharing a param overwrite each other's value, so only those opened after the one the URL still names are closed.
window.addEventListener('popstate', () => {
  const queryParams = new URI().query(true) || {};
  const withHistoryEntry = openedDialogs.filter((dialog) => dialog.queryParamValue);
  withHistoryEntry
    .filter((dialog, index) => !withHistoryEntry.slice(index).some((d) => d.queryParamName === dialog.queryParamName && queryParams[d.queryParamName] == d.queryParamValue))
    .forEach((dialog) => (dialog.opened = false));
});

/**
 * Provides a way to manage history for dialog.
 * 
 * ## Behaviour
 * - When `queryParamValue` is define 
 *  - If Dialog is opened and queryParamValue is not set as action then set action query params as a `queryParamValue` property using `setQueryParams` method of router.
 *  - If Dialog is closed and action query params is equal to `queryParamValue` then history back.
 */
export const DialogHistoryManager = (baseElement) => class extends baseElement {
  constructor(){
    super();
    this.queryParamValue = '';
    this.queryParamName = 'action';
  }

  static get properties() {
    return {

      /**
       * value of query param on which dialog should be opened.
       */
      queryParamValue: {type: String},

      /**
       * Name of query param, on which dialog should be opened.
       */
      queryParamName: { type: String }

    }
  }

  /**
   * When `queryParamValue` is define 
   *  If Dialog is opened and queryParamValue is not set as action then set action query params as a `queryParamValue` property using `setQueryParams` method of router.
   *  If Dialog is closed and action query params is equal to `queryParamValue` then history back.
   * @public
   */
  dialogHistoryManage() {
    if(!this.queryParamValue) {
      return;
    }

    const uri = new URI();
    const queryParams = uri.query(true) || [];
    const action = queryParams[this.queryParamName];

    if (this.opened && action != this.queryParamValue) {
      router.actions.setQueryParams({ [this.queryParamName]: this.queryParamValue }, true);
      return;
    }

    if (!this.opened && action == this.queryParamValue) {
      router.actions.back();
    }
  }

  // Tracked on `opened` and `close()`, not on the opened / closed events: those come after the animation, and a quick second back would still find this dialog on top.
  updated(changedProps) {
    super.updated && super.updated(changedProps);
    if (changedProps.has('opened')) {
      removeOpenedDialog(this);
      this.opened && openedDialogs.push(this);
    }
  }

  close() {
    removeOpenedDialog(this);
    super.close && super.close();
  }

  disconnectedCallback() {
    super.disconnectedCallback && super.disconnectedCallback();
    removeOpenedDialog(this);
  }
}